import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Konva from 'konva';
import Button from 'components/Basic/Button';
import { useRefs } from 'contexts/RefContext';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'store';
import { setKonvaStagePosition, setStageRotation } from 'store/slices/ToolSlice';
import { getLocalPoint, rotateStageAroundPivot } from 'components/tool/utils';

const RESET_NORTH_DURATION_MS = 300;
const NORTH_EPSILON_DEG = 0.01;

function pointNeedle(needle: SVGGElement | null, rotation: number) {
	needle?.setAttribute('transform', `rotate(${rotation} 16 16)`);
}

/** Needle SVG — rotation is applied imperatively via `needleRef` on every stage rotation change (no
 *  React re-render needed just to spin an icon), so it can track the live Konva stage rotation
 *  smoothly both while the user drags to rotate and while the reset-to-north animation plays. */
function CompassIcon({ needleRef }: { needleRef: React.RefObject<SVGGElement> }) {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
			<g ref={needleRef}>
				<path d="M16 3L21.3 16H10.7L16 3Z" fill="#E1442A"/>
				<path d="M10.7 16H21.3L16 29L10.7 16Z" fill="#9AA0A6"/>
			</g>
		</svg>
	);
}

/** Reset-to-north button for the 2D view. Doubles as a live compass needle: as soon as the user
 *  starts rotating the map it appears, and the needle tracks exactly how far (and which way) the
 *  view has turned from north in real time — both mid-drag and during this button's own reset
 *  animation — rather than just toggling a static icon on/off. */
export default function CompassButton() {
	const { konvaRef } = useRefs();
	const dispatch = useDispatch<AppDispatch>();
	const animRef = useRef<number | null>(null);
	const needleRef = useRef<SVGGElement>(null);
	const lastVisibleRef = useRef(false);
	const [isVisible, setIsVisible] = useState(false);

	// Konva fires rotationChange whenever the stage's rotation is set (mid-drag, mid-animation or a
	// reset), so the needle — and whether this button shows at all — follows the live stage without
	// polling every frame. Redux only updates once a gesture ends, so it can't drive this.
	useEffect(() => {
		let frameId: number | null = null;
		let stage: Konva.Stage | null = null;

		const update = () => {
			if (!stage) return;
			const rotation = stage.rotation();
			pointNeedle(needleRef.current, rotation);

			const nextVisible = Math.abs(rotation) >= NORTH_EPSILON_DEG;
			if (lastVisibleRef.current !== nextVisible) {
				lastVisibleRef.current = nextVisible;
				setIsVisible(nextVisible);
			}
		};

		const attach = () => {
			stage = konvaRef?.current ?? null;
			if (!stage) {
				frameId = requestAnimationFrame(attach); // stage not mounted yet
				return;
			}
			frameId = null;
			stage.on('rotationChange.compass', update);
			update();
		};

		attach();
		return () => {
			if (frameId !== null) cancelAnimationFrame(frameId);
			stage?.off('rotationChange.compass');
		};
	}, [konvaRef]);

	// The needle only mounts once the button shows: point it now, not on the next rotation change.
	useLayoutEffect(() => {
		pointNeedle(needleRef.current, konvaRef?.current?.rotation() ?? 0);
	}, [isVisible, konvaRef]);

	const handleClick = () => {
		const stage = konvaRef?.current;
		if (!stage) return;

		if (animRef.current != null) {
			cancelAnimationFrame(animRef.current);
			animRef.current = null;
		}

		const startRotation = stage.rotation();
		if (Math.abs(startRotation) < NORTH_EPSILON_DEG) return;

		// Take the shortest way back to north — after spinning the map around a few times,
		// `stage.rotation()` can be a large accumulated value (e.g. 745°); unwinding that raw
		// value would visibly spin through every one of those degrees instead of just turning
		// the short way to the nearest equivalent of north.
		let shortestDelta = startRotation % 360;
		if (shortestDelta > 180) shortestDelta -= 360;
		else if (shortestDelta < -180) shortestDelta += 360;

		const screenCenter = { x: stage.width() / 2, y: stage.height() / 2 };
		const pivotLocal = getLocalPoint(stage, screenCenter);
		const startTime = performance.now();

		const step = (now: number) => {
			const t = Math.min((now - startTime) / RESET_NORTH_DURATION_MS, 1);
			const eased = 1 - Math.pow(1 - t, 3);
			const rotation = startRotation - shortestDelta * eased;

			rotateStageAroundPivot(stage, pivotLocal, screenCenter, rotation);
			stage.batchDraw();

			if (t < 1) {
				animRef.current = requestAnimationFrame(step);
			} else {
				rotateStageAroundPivot(stage, pivotLocal, screenCenter, 0);
				stage.batchDraw();
				// rotateStageAroundPivot moved position on every frame of the animation — persist
				// the final position too, or Redux's konvaStagePosition goes stale right after this.
				dispatch(setKonvaStagePosition({ position: stage.position() }));
				dispatch(setStageRotation({ rotation: 0 }));
				animRef.current = null;
			}
		};

		animRef.current = requestAnimationFrame(step);
	};

	if (!isVisible) return null;

	return (
		<div className='compassbtn-wrap'>
			<Button
				id="compass-btn"
				className='zoombtnHover a' style={{
					fontSize: '0.938rem',
					borderRadius: '4px',
					fontWeight: '600',
					background: 'var(--controlBgColor)',
					border: 'transparent',
					color: 'var(--secondaryTextColor)',
					padding: '0 0 1px',
					boxShadow: '0px 0.5px 1.5px #0000001a',
					marginBottom: '10px',
					lineHeight:'38px',
					width: '38px',
					height: '38px',
				}}
				tooltipContent='Face North'
				onClick={handleClick}>
				<CompassIcon needleRef={needleRef} />
			</Button>
		</div>
	);
}
